/**
 * FormBuilder — Structured rule creation form (peer to NL input).
 *
 * Story 1.5 AC#4. "Build precisely" — first-class, not a fallback.
 *
 * Sequential flow: rule type → tables → columns → conditions.
 */

import { useState } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

const RULE_TYPES = [
  { value: "not_null", label: "Not Null" },
  { value: "unique", label: "Unique" },
  { value: "range", label: "Range Check" },
  { value: "pattern", label: "Pattern Match" },
  { value: "referential", label: "Referential Integrity" },
  { value: "aggregate", label: "Aggregate Check" },
  { value: "conditional", label: "Conditional" },
];

export interface FormBuilderProps {
  tables: string[];
  columnsForTable: (tableName: string) => string[];
  onSubmit: (rule: FormBuilderRule) => void;
  /** Pre-populated from partial NL parse */
  initialValues?: Partial<FormBuilderRule>;
  /** When set the user may not create rules: Create Rule is disabled and this
   * is shown beside it (e.g. "Requires the rules:create permission"). */
  disabledReason?: string | null;
}

export interface FormBuilderRule {
  ruleType: string;
  sourceTable: string;
  targetTable?: string;
  sourceColumn: string;
  targetColumn?: string;
  operator?: string;
  value?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FormBuilder({
  tables,
  columnsForTable,
  onSubmit,
  initialValues,
  disabledReason = null,
}: FormBuilderProps) {
  const [ruleType, setRuleType] = useState(initialValues?.ruleType || "");
  const [sourceTable, setSourceTable] = useState(
    initialValues?.sourceTable || "",
  );
  const [targetTable, setTargetTable] = useState(
    initialValues?.targetTable || "",
  );
  const [sourceColumn, setSourceColumn] = useState(
    initialValues?.sourceColumn || "",
  );
  const [targetColumn, setTargetColumn] = useState(
    initialValues?.targetColumn || "",
  );
  const [operator, setOperator] = useState(initialValues?.operator || "");
  const [value, setValue] = useState(initialValues?.value || "");

  const sourceCols = sourceTable ? columnsForTable(sourceTable) : [];
  const targetCols = targetTable ? columnsForTable(targetTable) : [];
  const needsTarget = ruleType === "referential";
  const needsOperator = [
    "range",
    "pattern",
    "aggregate",
    "conditional",
  ].includes(ruleType);

  const canSubmit = ruleType && sourceTable && sourceColumn;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || disabledReason) return;
    onSubmit({
      ruleType,
      sourceTable,
      targetTable: needsTarget ? targetTable : undefined,
      sourceColumn,
      targetColumn: needsTarget ? targetColumn : undefined,
      operator: needsOperator ? operator : undefined,
      value: needsOperator ? value : undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-body font-medium text-[var(--foreground)]">
        Build precisely
      </p>

      {/* Rule type */}
      <div>
        <label className="text-caption text-[var(--muted-foreground)] block mb-1">
          Rule Type
        </label>
        <select
          value={ruleType}
          onChange={(e) => setRuleType(e.target.value)}
          className="w-full px-3 py-2 rounded-lg glass-surface-input text-body text-[var(--foreground)] outline-none focus:ring-1 focus:ring-[var(--ring)]"
        >
          <option value="">Select rule type…</option>
          {RULE_TYPES.map((rt) => (
            <option key={rt.value} value={rt.value}>
              {rt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Source table + column */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-caption text-[var(--muted-foreground)] block mb-1">
            Source Table
          </label>
          <select
            value={sourceTable}
            onChange={(e) => {
              setSourceTable(e.target.value);
              setSourceColumn("");
            }}
            className="w-full px-3 py-2 rounded-lg glass-surface-input text-body text-[var(--foreground)] outline-none focus:ring-1 focus:ring-[var(--ring)]"
          >
            <option value="">Select table…</option>
            {tables.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-caption text-[var(--muted-foreground)] block mb-1">
            Source Column
          </label>
          <select
            value={sourceColumn}
            onChange={(e) => setSourceColumn(e.target.value)}
            disabled={!sourceTable}
            className="w-full px-3 py-2 rounded-lg glass-surface-input text-body text-[var(--foreground)] outline-none focus:ring-1 focus:ring-[var(--ring)] disabled:opacity-50"
          >
            <option value="">Select column…</option>
            {sourceCols.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Target table + column (referential only) */}
      {needsTarget && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-caption text-[var(--muted-foreground)] block mb-1">
              Target Table
            </label>
            <select
              value={targetTable}
              onChange={(e) => {
                setTargetTable(e.target.value);
                setTargetColumn("");
              }}
              className="w-full px-3 py-2 rounded-lg glass-surface-input text-body text-[var(--foreground)] outline-none focus:ring-1 focus:ring-[var(--ring)]"
            >
              <option value="">Select table…</option>
              {tables.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-caption text-[var(--muted-foreground)] block mb-1">
              Target Column
            </label>
            <select
              value={targetColumn}
              onChange={(e) => setTargetColumn(e.target.value)}
              disabled={!targetTable}
              className="w-full px-3 py-2 rounded-lg glass-surface-input text-body text-[var(--foreground)] outline-none focus:ring-1 focus:ring-[var(--ring)] disabled:opacity-50"
            >
              <option value="">Select column…</option>
              {targetCols.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Operator + value (range/pattern/aggregate/conditional) */}
      {needsOperator && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-caption text-[var(--muted-foreground)] block mb-1">
              Operator
            </label>
            <select
              value={operator}
              onChange={(e) => setOperator(e.target.value)}
              className="w-full px-3 py-2 rounded-lg glass-surface-input text-body text-[var(--foreground)] outline-none focus:ring-1 focus:ring-[var(--ring)]"
            >
              <option value="">Select…</option>
              <option value=">">&gt;</option>
              <option value=">=">&gt;=</option>
              <option value="<">&lt;</option>
              <option value="<=">&lt;=</option>
              <option value="==">==</option>
              <option value="!=">!=</option>
              <option value="matches">matches (regex)</option>
              <option value="between">between</option>
            </select>
          </div>
          <div>
            <label className="text-caption text-[var(--muted-foreground)] block mb-1">
              Value
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Threshold or pattern…"
              className="w-full px-3 py-2 rounded-lg glass-surface-input text-body text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] outline-none focus:ring-1 focus:ring-[var(--ring)]"
            />
          </div>
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={!canSubmit && !disabledReason}
        aria-disabled={disabledReason ? true : undefined}
        aria-describedby={disabledReason ? "form-builder-blocked" : undefined}
        title={disabledReason ?? undefined}
        className="px-4 py-2 rounded-lg bg-[var(--primary)] text-[var(--primary-foreground)] text-body font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50 aria-disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:hover:opacity-50"
      >
        Create Rule
      </button>
      {disabledReason && (
        <p
          id="form-builder-blocked"
          className="text-caption text-[var(--warning)]"
        >
          {disabledReason}
        </p>
      )}
    </form>
  );
}
